import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserCredits } from '@/hooks/useUserCredits';

interface AffirmationImageConfig {
  feature_enabled: boolean;
  free_lifetime_cap: number;
  starter_monthly_cap: number;
  connection_monthly_cap: number;
  legacy_monthly_cap: number;
}

interface GeneratedImage {
  id: string;
  image_url: string;
  affirmation_text: string;
  original_photo_path: string;
  created_at: string;
}

interface UsageInfo {
  generation_count: number;
  period_start: string;
}

export type PlanTier = 'free' | 'starter' | 'connection' | 'legacy';

function isSameMonth(dateString: string, referenceDate: Date) {
  const date = new Date(dateString);
  return date.getMonth() === referenceDate.getMonth() && date.getFullYear() === referenceDate.getFullYear();
}

function getArchiveStorageKey(userId?: string) {
  return userId ? `affirmation-archived:${userId}` : 'affirmation-archived:guest';
}

export function useAffirmationImages() {
  const { user } = useAuth();
  const { credits, isLoading: creditsLoading } = useUserCredits();
  const [config, setConfig] = useState<AffirmationImageConfig | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [gallery, setGallery] = useState<GeneratedImage[]>([]);
  const [archivedIds, setArchivedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPaid = credits?.has_purchased_bundle === true;

  const planTier: PlanTier = (() => {
    if (!credits?.has_purchased_bundle) return 'free';
    const bundle = credits.highest_bundle_purchased;
    if (bundle === 'legacy') return 'legacy';
    if (bundle === 'connection') return 'connection';
    return 'starter';
  })();

  const fetchData = useCallback(async (silent = false) => {
    if (!user || creditsLoading) return;
    if (!silent) setIsLoading(true);

    try {
      const [configRes, usageRes, galleryRes] = await Promise.all([
        supabase.from('affirmation_image_config').select('feature_enabled, free_lifetime_cap, starter_monthly_cap, connection_monthly_cap, legacy_monthly_cap').eq('id', 1).single(),
        supabase.from('affirmation_image_usage').select('*').eq('user_id', user.id).maybeSingle(),
        supabase.from('generated_affirmation_images').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      ]);

      if (configRes.data) setConfig(configRes.data as unknown as AffirmationImageConfig);

      const now = new Date();
      const currentPeriodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const galleryData = (galleryRes.data as GeneratedImage[]) ?? [];
      const historyCount = isPaid
        ? galleryData.filter((image) => isSameMonth(image.created_at, now)).length
        : galleryData.length;
      const usageData = usageRes.data as UsageInfo | null;
      const usageIsCurrentPeriod = usageData ? isSameMonth(usageData.period_start, now) : false;

      // Trust the DB generation_count as source of truth — admin grants
      // reduce this counter, so we must NOT override it with row counts.
      let nextUsage: UsageInfo;
      if (!usageData) {
        // First time: seed from actual history count (INSERT only; UPDATE is server-side)
        nextUsage = { generation_count: historyCount, period_start: isPaid ? currentPeriodStart : now.toISOString() };
        await supabase
          .from('affirmation_image_usage')
          .insert({ user_id: user.id, generation_count: nextUsage.generation_count, period_start: nextUsage.period_start });
      } else if (isPaid && !usageIsCurrentPeriod) {
        // New month for paid users — reset via SECURITY DEFINER RPC (users can't self-update)
        await (supabase as any).rpc('reset_affirmation_usage_period');
        nextUsage = { generation_count: 0, period_start: currentPeriodStart };
      } else {
        // Use the DB counter directly (respects admin grants)
        nextUsage = {
          generation_count: usageData.generation_count,
          period_start: usageData.period_start,
        };
      }


      setGallery(galleryData);
      setUsage(nextUsage);
    } catch (err) {
      console.error('Error fetching affirmation image data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, isPaid, creditsLoading]);

  useEffect(() => {
    if (!user) {
      setArchivedIds([]);
      return;
    }

    try {
      const rawValue = localStorage.getItem(getArchiveStorageKey(user.id));
      const parsed = rawValue ? JSON.parse(rawValue) : [];
      setArchivedIds(Array.isArray(parsed) ? parsed : []);
    } catch {
      setArchivedIds([]);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!creditsLoading) {
      fetchData();
    }
  }, [fetchData, creditsLoading]);

  useEffect(() => {
    if (!user || creditsLoading) return;

    const syncUsage = () => {
      if (document.visibilityState === 'hidden') return;
      fetchData(true);
    };

    window.addEventListener('focus', syncUsage);
    document.addEventListener('visibilitychange', syncUsage);

    return () => {
      window.removeEventListener('focus', syncUsage);
      document.removeEventListener('visibilitychange', syncUsage);
    };
  }, [fetchData, user?.id, creditsLoading]);

  const getCapForPlan = useCallback((): number => {
    if (!config) return 0;
    switch (planTier) {
      case 'free': return config.free_lifetime_cap;
      case 'starter': return config.starter_monthly_cap;
      case 'connection': return config.connection_monthly_cap;
      case 'legacy': return config.legacy_monthly_cap;
      default: return 0;
    }
  }, [config, planTier]);

  const remainingGenerations = useCallback(() => {
    if (!config || !usage) return 0;
    const cap = getCapForPlan();
    return Math.max(0, cap - usage.generation_count);
  }, [config, usage, getCapForPlan]);

  const canGenerate = useCallback(() => {
    if (!config?.feature_enabled) return false;
    return remainingGenerations() > 0;
  }, [config, remainingGenerations]);

  const archiveImage = useCallback((imageId: string) => {
    if (!user) return;

    setArchivedIds((prev) => {
      const next = Array.from(new Set([...prev, imageId]));
      localStorage.setItem(getArchiveStorageKey(user.id), JSON.stringify(next));
      return next;
    });
  }, [user?.id]);

  const restoreArchivedImages = useCallback(() => {
    if (!user) return;
    localStorage.removeItem(getArchiveStorageKey(user.id));
    setArchivedIds([]);
  }, [user?.id]);

  const deleteImage = useCallback(async (imageId: string, storagePath?: string) => {
    if (!user) throw new Error('Not authenticated');

    const { error: deleteError } = await supabase
      .from('generated_affirmation_images')
      .delete()
      .eq('id', imageId)
      .eq('user_id', user.id);

    if (deleteError) {
      throw new Error(deleteError.message || 'Failed to delete affirmation');
    }

    if (storagePath) {
      const { error: storageError } = await supabase.storage
        .from('affirmation-images')
        .remove([storagePath]);

      if (storageError) {
        console.warn('Failed to remove affirmation image from storage:', storageError.message);
      }
    }

    setGallery((prev) => prev.filter((image) => image.id !== imageId));
    setArchivedIds((prev) => {
      const next = prev.filter((id) => id !== imageId);
      localStorage.setItem(getArchiveStorageKey(user.id), JSON.stringify(next));
      return next;
    });
  }, [user?.id]);

  /** Convert a data-URL to a Blob for storage upload */
  const dataUrlToBlob = useCallback((dataUrl: string): Blob => {
    const [header, b64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }, []);

  /** Estimate binary bytes represented by a data-URL */
  const estimateDataUrlBytes = useCallback((dataUrl: string): number => {
    const b64 = dataUrl.split(',')[1] ?? '';
    if (!b64) return 0;
    const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
  }, []);

  const generateImage = useCallback(async (photoBase64: string, styleSettings?: Record<string, unknown>) => {
    if (!user) throw new Error('Not authenticated');

    console.log('[AffirmationFlow] button click received');
    setIsGenerating(true);
    setError(null);

    let sourceFilePath: string | null = null;

    try {
      console.log('[AffirmationFlow] image preprocessing/compression started');

      // Step 1: Upload source photo to storage at FULL quality
      const originalFileSizeBytes = estimateDataUrlBytes(photoBase64);
      const blob = dataUrlToBlob(photoBase64);

      console.log('[AffirmationFlow] image preprocessing/compression completed', {
        originalFileSizeBytes,
        compressedFileSizeBytes: blob.size,
        compressionApplied: false,
      });

      const ext = blob.type === 'image/png' ? 'png' : 'jpg';
      sourceFilePath = `${user.id}/sources/${crypto.randomUUID()}.${ext}`;

      console.log('[AffirmationGen] Uploading source photo to storage, size:', Math.round(blob.size / 1024), 'KB');

      const { error: uploadErr } = await supabase.storage
        .from('affirmation-images')
        .upload(sourceFilePath, blob, { contentType: blob.type, upsert: false });

      if (uploadErr) {
        console.error('[AffirmationGen] Source upload failed:', uploadErr.message);
        throw new Error('Failed to upload photo. Please try again.');
      }

      // Step 2: Get public URL for the uploaded source
      const { data: urlData } = supabase.storage
        .from('affirmation-images')
        .getPublicUrl(sourceFilePath);

      const sourceUrl = urlData.publicUrl;
      console.log('[AffirmationGen] Source uploaded, public URL obtained');

      // Step 3: Get a FRESH token
      const { data: refreshed } = await (supabase.auth as any).refreshSession();
      let token = refreshed.session?.access_token;
      if (!token) {
        const { data: fallback } = await (supabase.auth as any).getSession();
        token = fallback.session?.access_token;
      }
      if (!token) {
        throw new Error('Auth session expired. Please log in again.');
      }

      const payload = JSON.stringify({ photoUrl: sourceUrl, ...(styleSettings ? { style: styleSettings } : {}) });
      const payloadSizeBytes = new TextEncoder().encode(payload).length;
      console.log('[AffirmationFlow] final payload size', {
        payloadSizeBytes,
      });
      console.log('[AffirmationFlow] request sent to edge function');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-affirmation-image`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${token}`,
          },
          body: payload,
        }
      );

      console.log('[AffirmationFlow] edge function response received', {
        status: response.status,
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 429 && result?.error === 'limit_reached') {
          throw new Error('limit_reached');
        }

        const stage = typeof result?.stage === 'string' ? result.stage : 'edge_function_request';
        const stageErrorMap: Record<string, string> = {
          upload_preprocessing: 'Upload preprocessing failed',
          edge_function_request: 'Edge function request failed',
          provider_request: 'AI provider request failed',
          response_parse: 'AI response parsing failed',
          storage_upload: 'Storage upload failed',
          database_save: 'Database save failed',
        };

        const stageMessage = stageErrorMap[stage] ?? 'Generation failed';
        const detail = typeof result?.detail === 'string' ? `: ${result.detail}` : '';
        throw new Error(`${stageMessage}${detail}`);
      }

      // Update local usage immediately from response
      if (typeof result.used === 'number') {
        setUsage(prev => prev ? { ...prev, generation_count: result.used } : { generation_count: result.used, period_start: new Date().toISOString() });
      }

      console.log('[AffirmationFlow] success response returned to frontend', {
        hasImageUrl: Boolean(result.imageUrl),
        used: result.used,
      });

      // Refresh gallery and re-fetch usage from DB to ensure consistency
      const [galleryRes, usageRefresh] = await Promise.all([
        supabase.from('generated_affirmation_images').select('*').eq('user_id', user!.id).order('created_at', { ascending: false }),
        supabase.from('affirmation_image_usage').select('*').eq('user_id', user!.id).maybeSingle(),
      ]);
      if (galleryRes.data) setGallery(galleryRes.data as GeneratedImage[]);
      if (usageRefresh.data) setUsage(usageRefresh.data as UsageInfo);

      return {
        imageUrl: result.imageUrl,
        affirmationText: result.affirmationText,
      };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Generation failed';
      console.error('[AffirmationGen] Error:', message);
      setError(message);

      // Clean up source photo on failure
      if (sourceFilePath) {
        supabase.storage.from('affirmation-images').remove([sourceFilePath]).catch(() => {});
      }

      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, [user?.id, fetchData, dataUrlToBlob, estimateDataUrlBytes]);

  const visibleGallery = gallery.filter((image) => !archivedIds.includes(image.id));
  const archivedCount = gallery.length - visibleGallery.length;

  return {
    config,
    usage,
    gallery: visibleGallery,
    archivedCount,
    isLoading,
    isGenerating,
    error,
    isPaid,
    planTier,
    canGenerate,
    remainingGenerations,
    getCapForPlan,
    generateImage,
    archiveImage,
    restoreArchivedImages,
    deleteImage,
    refetch: fetchData,
  };
}
